import { useAtom } from '@reatom/npm-react'
import React from 'react'

import { AsyncStateN } from '@blade/std-lib'
import { useHapticFeedback } from '@blade/ui-kit'
import { userAtom } from '@root/entities/auth'
import { Deeplink, DeeplinkURL } from '@root/entities/deeplinking'
import {
  otherUserDelugeIdAtom,
  userProfileAtom,
  userProfileStateAtom,
} from '@root/entities/profile'
import { UserShort } from '@root/entities/user-short'
import { UserShortAvatar } from '@root/entities/user-short/ui/user-short-avatar'
import { useClipboard } from '@root/shared'

import {
  commentsDrawerDelugeIdAtom,
  relationDrawerDelugeIdAtom,
  relationTabAtom,
  userProfileTabAtom,
} from '../../atoms'
import { ProfileHeader as ProfileHeader_ } from '../common/profile-header'
import { ProfileHeaderSkeleton } from '../common/profile-header-skeleton'
import { ProfileRankTags } from '../common/profile-rank-tags'

import type { ProfileHeaderAction } from '../common/profile-header'

type Props = {}

export const UserProfileHeader: React.FC<Props> = (_props: Props) => {
  const [userProfile] = useAtom(userProfileAtom)
  const [profileState] = useAtom(userProfileStateAtom)
  const [otherUserDelugeId] = useAtom(otherUserDelugeIdAtom) // in case userProfileAtom fails
  const [user] = useAtom(userAtom) // in case self profile

  const [_commentsDrawer, setCommentsDrawerDelugeId] = useAtom(
    commentsDrawerDelugeIdAtom,
  )
  const isSelfProfile =
    otherUserDelugeId && user?.delugeId && otherUserDelugeId === user.delugeId
  const profile = isSelfProfile ? user : userProfile
  const isLoading =
    otherUserDelugeId &&
    !isSelfProfile &&
    AsyncStateN.matchWaiting(profileState ?? AsyncStateN.loading(), {
      Waiting: () => true,
      Error: () => false,
      Success: () => false,
    })

  const { copyToClipboard } = useClipboard()
  const haptics = useHapticFeedback()
  const [_userProfileTab, setUserProfileTab] = useAtom(userProfileTabAtom)
  const [_relationTab, setRelationTab] = useAtom(relationTabAtom)
  const [_relationDrawer, setRelationDrawerDelugeId] = useAtom(
    relationDrawerDelugeIdAtom,
  )

  const handleCommentsClick = () => {
    haptics.impactOccurred('light')
    setCommentsDrawerDelugeId(otherUserDelugeId)
  }

  const actions: Array<ProfileHeaderAction> = []

  if (otherUserDelugeId) {
    const url = DeeplinkURL.fromDeeplink(
      new Deeplink.UserProfile({ delugeId: otherUserDelugeId }),
    )

    actions.unshift({
      icon: 'share',
      onClick: () => copyToClipboard(url.toString()),
    })
  }

  if (isLoading) {
    return <ProfileHeaderSkeleton />
  }

  return (
    <ProfileHeader_
      actions={actions}
      avatar={
        <UserShortAvatar
          userShort={profile}
          size={60}
          withHypedLetter
          withPreview={false}
        />
      }
      title={profile ? UserShort.someName(profile) : ''}
      mainSubtitle={
        <ProfileRankTags
          trading={{
            count: userProfile?.tradeRank || '-', // 0 and undefined
          }}
          referral={{
            count: userProfile?.referralRank || '-', // 0 and undefined
          }}
        />
      }
      connections={{
        onFollowersClick: () => {
          setRelationDrawerDelugeId(otherUserDelugeId)
          setRelationTab('followers')
        },
        onFollowingsClick: () => {
          setRelationDrawerDelugeId(otherUserDelugeId)
          setRelationTab('followings')
        },
        followers: userProfile?.followers.length ?? '-',
        following: userProfile?.following.length ?? '-',
      }}
      counts={{
        onLikesClick: handleCommentsClick,
        onMentionsClick: handleCommentsClick,
        onCoinsClick: () => {
          haptics.impactOccurred('light')
          setUserProfileTab('coins')
        },
        coins: userProfile?.deployedTokensCount ?? '-',
        likes: userProfile?.likesCount ?? '-',
        mentions: userProfile?.mentionsCount ?? '-',
      }}
    />
  )
}
